import { Stack } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import CenterBox from '@/components/common/CenterBox';
import { useSession } from '@/components/session/useSession';
import { OrgMenu } from '@/layouts/dashboard/navbar/OrgMenu';
import { HYPHEN } from '@/utils/constants';

type Props = {
  isCollapse: boolean | undefined;
  onNavItemClick?: VoidFunction;
};

export function NavbarOrg({ isCollapse, onNavItemClick }: Props) {
  const {
    org,
    user,
    featureFlags: { SHOW_ORG_LOGO_IN_SIDEBAR },
  } = useSession();

  const name = `${user.firstName} ${user.lastName}`;
  const initials = (user.firstName.at(0) ?? '') + (user.lastName.at(0) ?? '');

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);
  const [orgBase64Logo, setOrgBase64Logo] = useState<string | null>(null);

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const orgLogo = org.logoUrl;

  useEffect(() => {
    if (orgLogo) {
      fetch(orgLogo)
        .then((response) => response.blob())
        .then((blob) => blob.text())
        .then((text) => {
          const url = `data:image/png;base64,${text}`;
          setOrgBase64Logo(url);
        });
    }
  }, [orgLogo]);

  return (
    <>
      {SHOW_ORG_LOGO_IN_SIDEBAR && orgBase64Logo ? (
        <CenterBox sx={{ mb: 1, px: isCollapse ? 0 : 3 }}>
          <img
            src={orgBase64Logo}
            alt={org.name}
            style={{ maxWidth: '100%', maxHeight: '60px', width: 'auto', height: 'auto', display: 'block' }}
          />
        </CenterBox>
      ) : null}
      <OrgMenu handleClosePopover={handleClosePopover} openPopover={openPopover} onNavItemClick={onNavItemClick} />
      <Stack
        direction="row"
        data-cy="cy-settings-button-open"
        onClick={handleOpenPopover}
        sx={(theme) => ({
          backgroundColor: openPopover && theme.palette.grey[500_16],
          borderRadius: 1,
          px: 1.5,
          py: 1.5,
          mb: 0.5,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.grey[500_8],
          },
        })}
      >
        <Avatar
          sx={{ bgcolor: 'warning.main', color: 'white', width: 48, height: 48, fontWeight: 700, fontSize: '14px' }}
          alt={name}
        >
          {initials}
        </Avatar>
        <Box
          sx={{
            ml: 1.5,
            width: isCollapse ? 'auto' : '100%',
            transition: (theme) =>
              theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter,
              }),
          }}
        >
          {isCollapse ? null : (
            <>
              <PTypography variant={'subtitle1'} noOverflow={{ maxWidth: 150, title: name }}>
                {name}
              </PTypography>
              {org.name ? (
                <PTypography variant={'body2'} noOverflow={{ maxWidth: 150, title: org.name }} color="secondary">
                  {org.name}
                </PTypography>
              ) : (
                HYPHEN
              )}
            </>
          )}
        </Box>
      </Stack>
    </>
  );
}
